/**
 * planBreakeven — the highest price at which a player is still worth targeting.
 *
 * DEFINITION: the highest price at which he still appears in the optimal build,
 * given the current roster shape, budget, price sheet and PPG sheet. With both
 * sheets live this is the payoff for having them — "Gibbs at 25 PPG is worth up
 * to $78" is a computed answer, not a feeling.
 *
 * 🔴 MEASURED AGAINST THE FREE OPTIMUM, exactly as cost-of-commitment is. Every
 * probe solves a plan with NOTHING committed, so a player you have already
 * placed does not trivially report the budget just because he is sitting in a
 * slot. The question is "what is he worth to this roster", not "is he in it".
 *
 * 🔴 BINARY SEARCH, NOT A SCAN. The predicate is monotone — raising his price
 * can only ever push him out, never pull him in — so ceil(log2(budget)) solves
 * find the boundary exactly. At $200 that is 8 solves against 200 for a linear
 * scan, and `solves` comes back in the result so a test can hold the line if
 * somebody replaces it with the obvious loop.
 *
 * 🔴 AND THE ANSWER ASSUMES THE REST OF THE MARKET BEHAVES. Every other player
 * is priced at his sheet-or-board number, so this answers "what is he worth to
 * this roster if everyone else goes at their expected price" — not "what will
 * he cost". The panel carries that caveat once, near the column header.
 */

import { optimizePlan } from "./planOptimizer";

/** The most solves a search may take: the binary search bound, plus one. */
export const maxSolvesFor = (budget) => Math.ceil(Math.log2(Math.max(2, budget) + 1)) + 1;

/**
 * @returns {{ breakeven: number, solves: number }} breakeven 0 means "not worth
 *   targeting at any price" — he does not make the build even at $1.
 */
export function breakevenFor(player, { rosterSlots, pool, priceOf, valueOf, budget }) {
  const key = `${player.name}-${player.pos}`;
  const cap = Math.max(1, Math.floor(Number(budget) || 0));
  let solves = 0;

  // Is he in the optimal build when his price is forced to `p`? Everyone else
  // keeps their sheet-or-board price.
  const inBuild = (p) => {
    solves += 1;
    const priced = (q) => (`${q.name}-${q.pos}` === key ? p : priceOf(q));
    const r = optimizePlan({
      plan: { buckets: {} }, rosterSlots, pool, priceOf: priced, valueOf, budget: cap,
    });
    if (!r.ok) return false;
    return r.assignments.some((a) => `${a.player.name}-${a.player.pos}` === key);
  };

  // Invariant: he makes the build at `lo` (or lo is 0, meaning never), and does
  // not at hi+1. Converges on the largest price that still keeps him.
  let lo = 0;
  let hi = cap;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (inBuild(mid)) lo = mid; else hi = mid - 1;
  }
  return { breakeven: lo, solves };
}

export default breakevenFor;
